import { inject, Injectable } from '@angular/core';

import {
  NOTIFICATION_USE_CASES,
  NotificationUseCases
} from '@app/domain/use-cases/notification/notification.use-cases.contract';
import { UserUseCases, USER_USE_CASES } from '@app/domain/use-cases/user/user.use-cases.contract';
import { ISocketMessage } from '@app/entities/interfaces/message/socket-message.interface';
import { MessageService } from '@app/services/message/message.service';

import { MLoggerService } from '@mercadona/core/logger';
import { MTranslateService } from '@mercadona/core/translate';

@Injectable({
  providedIn: 'root'
})
export class WebSocketManagerService {
  #socketHandlers: Record<string, (message: ISocketMessage) => void> = {};
  #userUseCases: UserUseCases = inject(USER_USE_CASES);
  #notificationsUseCases: NotificationUseCases = inject(NOTIFICATION_USE_CASES);
  #mLoggerService: MLoggerService = inject(MLoggerService);
  #mTranslateService: MTranslateService = inject(MTranslateService);
  #messagesService: MessageService = inject(MessageService);

  initWebSocketManagement(userId: string) {
    this.#setSocketHandlers();
    this.#initMessagesServiceSubscription(userId);
  }

  #initMessagesServiceSubscription(userId: string) {
    this.#messagesService.init(userId);
    this.#messagesService.messages$.subscribe((message: ISocketMessage) => {
      this.#mLoggerService.info('Message Service: new message:', message);
      this.#onMessage(message);
    });
  }

  #setSocketHandlers() {
    this.#socketHandlers = {
      LANGUAGE_UPDATED: (actionMessage: ISocketMessage) => {
        this.#handleLanguageChange(actionMessage.data.language_code === 'pt' ? 'pt' : 'es');
      },
      IMAGE_UPDATED: () => {
        this.#handleImageChange();
      },
      NOTIFICATION_CREATED: () => {
        this.#fetchNotifications();
      },
      NOTIFICATION_REMOVED: (actionMessage: ISocketMessage) => {
        this.#handleNotificationRemoved(actionMessage.data.id);
      },
      NOTIFICATION_READ: (actionMessage: ISocketMessage) => {
        this.#handleNotificationRead(actionMessage.data.id);
      }
    };
  }

  #handleNotificationRead(id: string) {
    this.#notificationsUseCases.markNotificationAsRead(id);
  }

  #handleNotificationRemoved(id: string) {
    this.#notificationsUseCases.removeFetchedNotification(id);
  }

  #fetchNotifications() {
    this.#notificationsUseCases.fetchNotifications(this.#mTranslateService.getActiveLang());
  }

  #handleLanguageChange(language_code: string) {
    this.#mTranslateService.setActiveLang(language_code);
    this.#userUseCases.storeLanguage(language_code);
  }

  #handleImageChange() {
    this.#userUseCases.syncImage().catch((error) => {
      this.#mLoggerService.error('Error retrieving image', error);
    });
  }

  #onMessage(message: ISocketMessage) {
    const handler = this.#socketHandlers[message.action];
    if (handler) {
      handler(message);
    }
  }
}
